import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as s from '../Table.css'
import * as cs from './DateRangeChip.css'
import { TablePopover } from './TablePopover'
import { TabSwitcher } from '../../TabSwitcher'
import { useT } from "../../../../app/lib/i18n";

function fmtChipDate(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtChipDateTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0 || d.getMilliseconds() !== 0
  if (!hasTime) return fmtChipDate(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function isoToDate(iso: string): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? null : d
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function isToday(d: Date): boolean {
  return sameDay(d, new Date())
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

const PRESETS = [
  { id: 'today', label: 'Today', kind: 'today' as const },
  { id: 'day', label: 'Last 24 hours', kind: 'relative' as const, ms: 24 * 60 * 60 * 1000 },
  { id: 'week', label: 'Last 7 days', kind: 'relative' as const, ms: 7 * 24 * 60 * 60 * 1000 },
  { id: 'month', label: 'Last 30 days', kind: 'relative' as const, ms: 30 * 24 * 60 * 60 * 1000 },
  { id: 'year', label: 'Last year', kind: 'relative' as const, ms: 365 * 24 * 60 * 60 * 1000 },
] as const

const CUSTOM_ID = 'custom'

/** Local calendar day: [today 12:00 AM, tomorrow 12:00 AM). */
function todayRange(now = new Date()): [string, string] {
  const start = startOfDay(now)
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1)
  return [start.toISOString(), end.toISOString()]
}

function relativeRange(ms: number): [string, string] {
  const end = new Date()
  const start = new Date(end.getTime() - ms)
  return [start.toISOString(), end.toISOString()]
}

function matchesPreset(startISO: string, endISO: string, ms: number, slackMs = 90_000): boolean {
  if (!startISO || !endISO) return false
  const start = Date.parse(startISO)
  const end = Date.parse(endISO)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false
  const span = end - start
  const endAge = Date.now() - end
  return Math.abs(span - ms) <= slackMs && endAge >= 0 && endAge <= slackMs * 2
}

function matchesTodayPreset(startISO: string, endISO: string, slackMs = 90_000): boolean {
  if (!startISO || !endISO) return false
  const [expStart, expEnd] = todayRange()
  const start = Date.parse(startISO)
  const end = Date.parse(endISO)
  const expectedStart = Date.parse(expStart)
  const expectedEnd = Date.parse(expEnd)
  if (![start, end, expectedStart, expectedEnd].every(Number.isFinite)) return false
  return Math.abs(start - expectedStart) <= slackMs && Math.abs(end - expectedEnd) <= slackMs
}

export function DateRangeChip({
  label,
  value,
  onChange,
  presentation = 'popover',
}: {
  label: string
  value: string[] | null
  onChange: (v: string[] | null) => void
  presentation?: 'popover' | 'inline'
}) {
  const t = useT()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('start')
  const [hoverDay, setHoverDay] = useState<Date | null>(null)
  /** User chose Custom in the dropdown (may still match a preset until dates change). */
  const [customMode, setCustomMode] = useState(false)

  const startISO = value?.[0] ?? ''
  const endISO = value?.[1] ?? ''
  const startDate = useMemo(() => isoToDate(startISO), [startISO])
  const endDate = useMemo(() => isoToDate(endISO), [endISO])
  const active = !!(startISO || endISO)

  const activePresetId = useMemo(() => {
    if (matchesTodayPreset(startISO, endISO)) return 'today'
    for (const p of PRESETS) {
      if (p.kind === 'relative' && matchesPreset(startISO, endISO, p.ms)) return p.id
    }
    return null
  }, [startISO, endISO])

  const showCalendar = customMode || (active && !activePresetId)

  const selectValue = showCalendar
    ? CUSTOM_ID
    : (activePresetId ?? '')

  // When the popover opens, sync custom mode from the current value.
  useEffect(() => {
    if (open) setCustomMode(active && !activePresetId)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps -- only on open

  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const ref = startDate ?? endDate ?? new Date()
    return new Date(ref.getFullYear(), ref.getMonth(), 1)
  })

  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const firstDow = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells: (Date | null)[] = []
    for (let i = 0; i < firstDow; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [viewMonth])

  const prevMonth = () => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))
  const nextMonth = () => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))

  const handleDayClick = (day: Date) => {
    const dayStart = startOfDay(day)
    if (activeTab === 'start') {
      const newEnd = endDate && startOfDay(endDate) >= dayStart ? endISO : ''
      onChange([dayStart.toISOString(), newEnd])
      setActiveTab('end')
    } else {
      const dayEnd = endOfDay(day)
      if (startDate && dayStart < startOfDay(startDate)) {
        onChange([dayStart.toISOString(), endOfDay(startDate).toISOString()])
      } else {
        onChange([startISO, dayEnd.toISOString()])
      }
      setOpen(false)
    }
  }

  const handleSelectChange = (id: string) => {
    if (id === CUSTOM_ID) {
      setCustomMode(true)
      setActiveTab('start')
      const ref = startDate ?? endDate ?? new Date()
      setViewMonth(new Date(ref.getFullYear(), ref.getMonth(), 1))
      return
    }
    const preset = PRESETS.find(p => p.id === id)
    if (!preset) return
    setCustomMode(false)
    if (preset.kind === 'today') onChange(todayRange())
    else onChange(relativeRange(preset.ms))
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setCustomMode(false)
    setActiveTab('start')
  }

  const chipValueText = useMemo(() => {
    if (!showCalendar && activePresetId) {
      return PRESETS.find(p => p.id === activePresetId)?.label ?? ''
    }
    if (showCalendar) {
      const sLabel = fmtChipDateTime(startISO)
      const eLabel = fmtChipDateTime(endISO)
      if (sLabel && eLabel) return `${sLabel} – ${eLabel}`
      if (sLabel) return `From ${sLabel}`
      if (eLabel) return `To ${eLabel}`
      return t("shared.table.customRange")
    }
    return ''
  }, [startISO, endISO, activePresetId, showCalendar, t])

  const getDayClass = (day: Date): string => {
    const classes: string[] = [cs.calendarDay]
    if (isToday(day)) classes.push(cs.calendarDayToday)

    const isStart = startDate != null && sameDay(day, startDate)
    const isEnd = endDate != null && sameDay(day, endDate)

    if (isStart || isEnd) {
      classes.push(cs.calendarDaySelected)
    } else if (startDate) {
      const dayS = startOfDay(day)
      const startS = startOfDay(startDate)
      const rangeEnd = endDate
        ? startOfDay(endDate)
        : activeTab === 'end' && hoverDay
        ? startOfDay(hoverDay)
        : null
      if (rangeEnd && dayS > startS && dayS < rangeEnd) {
        classes.push(cs.calendarDayInRange)
      } else if (!endDate && activeTab === 'end' && hoverDay) {
        const hoverS = startOfDay(hoverDay)
        if (hoverS < startS && dayS > hoverS && dayS < startS) {
          classes.push(cs.calendarDayInRange)
        }
      }
    }

    return classes.join(' ')
  }

  const panel = (
        <div className={cs.calendarWrap}>
          <label className={cs.presetSelectLabel}>
            <span className={cs.presetSelectCaption}>{t("shared.table.range")}</span>
            <select
              className={cs.presetSelect}
              value={selectValue}
              aria-label={t("shared.table.dateRangePreset")}
              onChange={(e) => handleSelectChange(e.target.value)}
            >
              {!selectValue && (
                <option value="" disabled>
                  Select a range
                </option>
              )}
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value={CUSTOM_ID}>{t("shared.table.customRange")}</option>
            </select>
          </label>

          {showCalendar && (
            <>
              <div className={cs.tabRow}>
                <TabSwitcher
                  tabs={[
                    { value: 'start', label: 'Start date' },
                    { value: 'end', label: 'End date' },
                  ]}
                  value={activeTab}
                  onChange={setActiveTab}
                  aria-label={t("shared.table.selectStartOrEndDate")}
                />
              </div>

              <div className={cs.calendarHeader}>
                <button type="button" className={cs.calendarNav} onClick={prevMonth} aria-label={t("shared.table.previousMonth")}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <span className={cs.calendarTitle}>{fmtMonthYear(viewMonth)}</span>
                <button type="button" className={cs.calendarNav} onClick={nextMonth} aria-label={t("shared.table.nextMonth")}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>

              <div className={cs.calendarGrid}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                  <div key={d} className={cs.calendarDayHead}>{d}</div>
                ))}
                {calendarCells.map((day, i) =>
                  day == null ? (
                    <div key={i} className={cs.calendarDayEmpty} />
                  ) : (
                    <button
                      key={i}
                      type="button"
                      className={getDayClass(day)}
                      onClick={() => handleDayClick(day)}
                      onMouseEnter={() => setHoverDay(day)}
                      onMouseLeave={() => setHoverDay(null)}
                      aria-label={day.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    >
                      {day.getDate()}
                    </button>
                  )
                )}
              </div>
            </>
          )}

          {(active || showCalendar) && (
            <>
              <div className={s.popoverDivider} />
              <div className={s.popoverFoot}>
                <button type="button" className={s.popoverFootBtn} onClick={handleClear}>
                  Clear
                </button>
                <button type="button" className={s.popoverFootBtn} onClick={() => setOpen(false)}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
  )

  if (presentation === 'inline') {
    return (
      <div className={s.filterSheetControl}>
        <button
          ref={anchorRef}
          type="button"
          className={`${s.sortConfigBtn} ${s.filterSheetTrigger}${open || active ? ' is-active' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {!active && <PlusIcon />}
          <span className={s.filterChipLabel}>{label}</span>
          {active && chipValueText ? (
            <span className={s.filterSheetTriggerValue}>{chipValueText}</span>
          ) : null}
        </button>
        {open ? <div className={s.filterSheetInlinePanel}>{panel}</div> : null}
      </div>
    )
  }

  return (
    <div className={s.popoverAnchor}>
      <button
        ref={anchorRef}
        type="button"
        className={`${s.filterChip}${active ? ' is-active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {!active && <PlusIcon />}
        <span className={s.filterChipLabel}>{label}</span>
        {active && chipValueText && (
          <span className={s.filterChipValues}>
            <span>{chipValueText}</span>
          </span>
        )}
      </button>
      <TablePopover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        {panel}
      </TablePopover>
    </div>
  )
}

function PlusIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
